// src/screens/SettingsScreen.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  OutlinedInput,
  IconButton,
  Dialog,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
} from '@mui/material';
import {
  Flag as FlagIcon,
  CalendarMonth as CalendarIcon,
  Store as StoreIcon,
  Category as CategoryIcon,
  Storage as StorageIcon,
  AddCircle as AddCircleIcon,
  Delete as DeleteIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
  Receipt as ReceiptIcon,
  Analytics as AnalyticsIcon,
  DateRange as DateRangeIcon,
  Today as TodayIcon,
  EventAvailable as EventAvailableIcon,
  EventBusy as EventBusyIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Paid as PaidIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import {
  getAllDeliveries,
  getMonthlyTarget,
  getAllRestDays,
  setMonthlyTarget,
  getAllPlatforms,
  getAllExpenseCategories,
  addPlatform,
  deletePlatform,
  addExpenseCategory,
  deleteExpenseCategory,
  saveRestDaysForMonth,
  getAllExpenses,
} from '../services/dataService';
import { DeliveryRecord, RestDay, DeliveryPlatform, ExpenseCategory } from '../types';

interface Props {
  onShowSnackbar: (message: string, severity?: 'success' | 'error') => void;
}

type TabKey = 'target' | 'rest' | 'platform' | 'expense' | 'data';

const iconButtonStyle = {
  height: 80,
  borderRadius: '12px',
  bgcolor: '#2a2a2a',
  color: '#888',
  px: 2,
  display: 'flex',
  flexDirection: 'column' as const,
  alignItems: 'center',
  justifyContent: 'center',
  gap: 0.5,
  cursor: 'pointer',
  '&.active': {
    bgcolor: '#10B981',
    color: '#000',
    boxShadow: '0 4px 8px rgba(16,185,129,0.4)',
    fontWeight: 700,
  },
};

function fmtJPY(n: number) {
  return `¥${n.toLocaleString()}`;
}

function ymd(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function isSameMonth(date: Date, year: number, month: number) {
  return date.getFullYear() === year && date.getMonth() === month;
}

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}

function SettingsScreen({ onShowSnackbar }: Props) {
  const [tab, setTab] = useState<TabKey>('target');
  const [loading, setLoading] = useState(true);
  
  const [deliveries, setDeliveries] = useState<DeliveryRecord[]>([]);
  const [monthlyTarget, setMonthlyTargetInput] = useState('150000');
  const [currentTarget, setCurrentTarget] = useState(150000);
  const [restDays, setRestDays] = useState<Date[]>([]);
  const [calendarRestDays, setCalendarRestDays] = useState<Date[]>([]);
  const [platforms, setPlatforms] = useState<DeliveryPlatform[]>([]);
  const [expenseCategories, setExpenseCategories] = useState<ExpenseCategory[]>([]);

  const [showAddPlatform, setShowAddPlatform] = useState(false);
  const [platformName, setPlatformName] = useState('');

  const [showAddCategory, setShowAddCategory] = useState(false);
  const [categoryName, setCategoryName] = useState('');
  const [categoryIcon, setCategoryIcon] = useState('💰');
  const categoryIcons = [
    '💰', '⛽', '🔧', '🛡️', '🅿️', '🍱', '📱', '🎒',
    '🚗', '🏠', '⚡', '💡', '🛒', '🎓', '🥗', '💊',
    '🎮', '📚', '✈️', '🎬', '☕', '🍕', '👕', '🎁',
  ];

  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const [pendingImportFile, setPendingImportFile] = useState<File | null>(null);
  const [showImportChoice, setShowImportChoice] = useState(false);
  const [replaceExisting, setReplaceExisting] = useState(false);

  const [openCal, setOpenCal] = useState(false);
  const [calYM, setCalYM] = useState<{ y: number; m: number }>(() => {
    const now = new Date();
    return { y: now.getFullYear(), m: now.getMonth() };
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (!openCal) return;
    setCalendarRestDays(restDays.filter((d) => isSameMonth(d, calYM.y, calYM.m)));
  }, [openCal, calYM.y, calYM.m, restDays]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [deliveriesData, target, restDaysData, platformsData, categoriesData] =
        await Promise.all([
          getAllDeliveries(),
          getMonthlyTarget(),
          getAllRestDays(),
          getAllPlatforms(),
          getAllExpenseCategories(),
        ]);
      setDeliveries(deliveriesData);
      setCurrentTarget(target);
      setMonthlyTargetInput(target.toString());
      setRestDays(restDaysData.map((d: RestDay) => new Date(d.date)));
      setPlatforms(platformsData);
      setExpenseCategories(categoriesData);
    } catch (error) {
      onShowSnackbar('データの読み込みに失敗しました', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTarget = async () => {
    try {
      const target = parseInt(monthlyTarget) || currentTarget;
      await setMonthlyTarget(target);

      setCurrentTarget(target);
      onShowSnackbar('設定を保存しました', 'success');
    } catch (error) {
      onShowSnackbar('保存に失敗しました', 'error');
    }
  };

  const handleExportData = () => {
    const data = {
      deliveries,
      monthlyTarget: currentTarget,
      restDays: restDays.map(ymd),
      platforms,
      expenseCategories,
      exportDate: new Date().toISOString(),
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const ts = new Date().toISOString().replace(/[-:T.Z]/g, '').slice(0, 14);
    a.href = url;
    a.download = `delivery_data_${ts}.json`;
    a.click();
    URL.revokeObjectURL(url);

    onShowSnackbar('データをエクスポートしました', 'success');
  };

  const handleImportData = async () => {
    if (!pendingImportFile) return;
    try {
      const text = await pendingImportFile.text();
      const data = JSON.parse(text);
      onShowSnackbar(
        replaceExisting ? 'データを置き換えました' : 'データを追加しました',
        'success'
      );
      await loadData();
    } catch (error) {
      onShowSnackbar('インポートに失敗しました', 'error');
    } finally {
      setShowImportChoice(false);
      setPendingImportFile(null);
      setReplaceExisting(false);
    }
  };

  const handleExportTaxCSV = async () => {
    try {
      const expenses = await getAllExpenses();
      
      // BOM付きUTF-8で出力（文字化け防止）
      const BOM = '\uFEFF';
      let csv = BOM + '日付,取引内容,収入金額,勘定科目,経費金額,摘要\n';

      deliveries.forEach((d) => {
        csv += `${d.date},${d.platform}配達売上,${d.totalRevenue},売上高,0,${d.deliveryCount}件\n`;
      });

      expenses.forEach((e) => {
        csv += `${e.date},${e.category},0,${e.category},${e.amount},${e.description}\n`;
      });

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const ts = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      a.href = url;
      a.download = `確定申告_収支記録_${ts}.csv`;
      a.click();
      URL.revokeObjectURL(url);

      onShowSnackbar('確定申告用CSVを出力しました', 'success');
    } catch (error) {
      onShowSnackbar('CSV出力に失敗しました', 'error');
    }
  };

  // 弥生会計用CSV出力
  const handleExportYayoiCSV = async () => {
    try {
      const expenses = await getAllExpenses();
      
      const BOM = '\uFEFF';
      let csv = BOM + '伝票No,決算,取引日,借方勘定科目,借方補助科目,借方部門,借方税区分,借方金額,借方税額,貸方勘定科目,貸方補助科目,貸方部門,貸方税区分,貸方金額,貸方税額,摘要,期日,証No,入力マシン,入力ユーザ,入力アプリ,入力会社,入力日付\n';
      
      let slipNo = 1;

      // 配達収入
      deliveries.forEach((d) => {
        csv += `${slipNo},,${d.date},売掛金,,,,${d.totalRevenue},0,売上高,,,,${d.totalRevenue},0,${d.platform}配達売上 ${d.deliveryCount}件,,,,,,,,\n`;
        slipNo++;
      });

      // 支出
      expenses.forEach((e) => {
        const account = e.category === 'ガソリン代' ? '車両費' : 
                       e.category === '修理費' ? '修繕費' :
                       e.category === '保険料' ? '保険料' :
                       e.category === '駐車場代' ? '地代家賃' :
                       e.category === '通信費' ? '通信費' : '雑費';
        csv += `${slipNo},,${e.date},${account},,,,${e.amount},0,現金,,,,${e.amount},0,${e.description},,,,,,,,\n`;
        slipNo++;
      });

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const ts = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      a.href = url;
      a.download = `弥生会計_${ts}.csv`;
      a.click();
      URL.revokeObjectURL(url);

      onShowSnackbar('弥生会計用CSVを出力しました', 'success');
    } catch (error) {
      onShowSnackbar('CSV出力に失敗しました', 'error');
    }
  };

  // freee会計用CSV出力
  const handleExportFreeeCSV = async () => {
    try {
      const expenses = await getAllExpenses();
      
      const BOM = '\uFEFF';
      let csv = BOM + '収支区分,管理番号,発生日,決済期日,取引先,勘定科目,税区分,金額,税込金額,備考,品目,部門,メモタグ,セグメント1,セグメント2,セグメント3\n';

      // 配達収入
      deliveries.forEach((d) => {
        csv += `収入,,${d.date},,${d.platform},売上高,課税売上10%,${d.totalRevenue},${d.totalRevenue},配達売上 ${d.deliveryCount}件,,,,,,\n`;
      });

      // 支出
      expenses.forEach((e) => {
        const account = e.category === 'ガソリン代' ? '車両費' : 
                       e.category === '修理費' ? '修繕費' :
                       e.category === '保険料' ? '保険料' :
                       e.category === '駐車場代' ? '地代家賃' :
                       e.category === '通信費' ? '通信費' : '雑費';
        csv += `支出,,${e.date},,経費,${account},課税仕入10%,${e.amount},${e.amount},${e.description},,,,,,\n`;
      });

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const ts = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      a.href = url;
      a.download = `freee会計_${ts}.csv`;
      a.click();
      URL.revokeObjectURL(url);

      onShowSnackbar('freee会計用CSVを出力しました', 'success');
    } catch (error) {
      onShowSnackbar('CSV出力に失敗しました', 'error');
    }
  };

  const target = parseInt(monthlyTarget) || 150000;
  const now = new Date();
  const currentMonthRestDays = restDays.filter((d) => isSameMonth(d, now.getFullYear(), now.getMonth()));
  const visibleMonthRestDays = restDays.filter((d) => isSameMonth(d, calYM.y, calYM.m));
  const workingDays = Math.max(1, getDaysInMonth(now.getFullYear(), now.getMonth()) - currentMonthRestDays.length);
  const dailyTarget = Math.floor(target / workingDays);
  const weeklyTarget = Math.floor(target / 4);

  const calendarSelectedSet = new Set(calendarRestDays.map(ymd));
  const daysInMonth = getDaysInMonth(calYM.y, calYM.m);
  const firstDow = new Date(calYM.y, calYM.m, 1).getDay();

  const openRestCalendar = () => {
    setCalendarRestDays(restDays.filter((d) => isSameMonth(d, calYM.y, calYM.m)));
    setOpenCal(true);
  };

  const toggleDate = (d: number) => {
    const date = new Date(calYM.y, calYM.m, d);
    const key = ymd(date);
    const next = calendarRestDays.filter((rd) => ymd(rd) !== key);
    if (next.length === calendarRestDays.length) {
      next.push(date);
    }
    setCalendarRestDays(next);
  };

  const handleSaveRestDays = async () => {
    try {
      await saveRestDaysForMonth(calendarRestDays, calYM.y, calYM.m);
      const otherMonthRestDays = restDays.filter((d) => !isSameMonth(d, calYM.y, calYM.m));
      setRestDays([...otherMonthRestDays, ...calendarRestDays].sort((a, b) => +a - +b));
      setOpenCal(false);
      onShowSnackbar('休日を保存しました', 'success');
    } catch (error) {
      onShowSnackbar('休日の保存に失敗しました', 'error');
    }
  };

  const handleCancelRestDays = () => {
    setCalendarRestDays(restDays.filter((d) => isSameMonth(d, calYM.y, calYM.m)));
    setOpenCal(false);
  };

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="white">読み込み中...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, pb: 10 }}>
      {/* カード型タブ */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 1, mb: 2 }}>
        <Box
          onClick={() => setTab('target')}
          sx={{ ...iconButtonStyle, ...(tab === 'target' && { bgcolor: '#10B981', color: '#000' }) }}
        >
          <FlagIcon />
          <Typography fontSize={14}>目標</Typography>
        </Box>
        <Box
          onClick={() => setTab('rest')}
          sx={{ ...iconButtonStyle, ...(tab === 'rest' && { bgcolor: '#10B981', color: '#000' }) }}
        >
          <CalendarIcon />
          <Typography fontSize={14}>休日</Typography>
        </Box>
        <Box
          onClick={() => setTab('platform')}
          sx={{ ...iconButtonStyle, ...(tab === 'platform' && { bgcolor: '#10B981', color: '#000' }) }}
        >
          <StoreIcon />
          <Typography fontSize={14}>会社名</Typography>
        </Box>
        <Box
          onClick={() => setTab('expense')}
          sx={{ ...iconButtonStyle, ...(tab === 'expense' && { bgcolor: '#10B981', color: '#000' }) }}
        >
          <CategoryIcon />
          <Typography fontSize={14}>支出</Typography>
        </Box>
        <Box
          onClick={() => setTab('data')}
          sx={{ ...iconButtonStyle, ...(tab === 'data' && { bgcolor: '#10B981', color: '#000' }) }}
        >
          <StorageIcon />
          <Typography fontSize={14}>データ</Typography>
        </Box>
      </Box>

      {/* コンテンツエリア */}
      <Box sx={{ minHeight: 400 }}>
        {tab === 'target' && (
          <Box>
            <Typography variant="h6" fontWeight="bold" mb={1} color="white">
              💰 月次目標
            </Typography>
            <Typography fontSize={15} color="#888" mb={2.5}>
              毎月の収益目標を設定してください
            </Typography>

            <OutlinedInput
              fullWidth
              type="number"
              value={monthlyTarget}
              onChange={(e) => setMonthlyTargetInput(e.target.value)}
              startAdornment={<PaidIcon sx={{ mr: 1, color: '#10B981' }} />}
              endAdornment={<Typography sx={{ ml: 1, color: '#888' }}>円</Typography>}
              sx={{
                mb: 2.5,
                color: 'white',
                bgcolor: '#2a2a2a',
                borderRadius: '12px',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#444' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#10B981' },
              }}
            />

            <Card sx={{ bgcolor: '#0f3d2c', borderRadius: '16px', mb: 2.5 }}>
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <AnalyticsIcon sx={{ color: '#10B981', fontSize: 20 }} />
                  <Typography fontWeight="bold" color="#10B981">
                    目標分析
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TodayIcon sx={{ color: '#10B981', fontSize: 18 }} />
                    <Typography fontSize={15} color="#ccc">
                      日次目標
                    </Typography>
                  </Box>
                  <Typography fontSize={18} fontWeight="bold" color="#10B981">
                    {fmtJPY(dailyTarget)}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DateRangeIcon sx={{ color: '#3B82F6', fontSize: 18 }} />
                    <Typography fontSize={15} color="#ccc">
                      週次目標
                    </Typography>
                  </Box>
                  <Typography fontSize={18} fontWeight="bold" color="#3B82F6">
                    {fmtJPY(weeklyTarget)}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography fontSize={15} color="#ccc">
                      稼働予定
                    </Typography>
                    {currentMonthRestDays.length > 0 && (
                      <Typography fontSize={13} color="#888">
                        休日: {currentMonthRestDays.length}日
                      </Typography>
                    )}
                  </Box>
                  <Typography fontSize={18} fontWeight="bold" color="#F59E0B">
                    {workingDays}日/月
                  </Typography>
                </Box>
              </CardContent>
            </Card>

            <Button
              fullWidth
              variant="contained"
              startIcon={<CheckIcon />}
              onClick={handleSaveTarget}
              sx={{
                height: 52,
                bgcolor: '#10B981',
                color: '#000',
                fontWeight: 'bold',
                borderRadius: '12px',
                '&:hover': { bgcolor: '#0d9668' },
              }}
            >
              保存する
            </Button>
          </Box>
        )}

        {tab === 'rest' && (
          <Box>
            <Typography variant="h6" fontWeight="bold" mb={1} color="white">
              📅 休日設定
            </Typography>
            <Typography fontSize={15} color="#888" mb={2.5}>
              月次目標の日割り計算に使用します
            </Typography>

            <Card
              sx={{
                bgcolor: visibleMonthRestDays.length > 0 ? '#0f3d2c' : '#2a2a2a',
                borderRadius: '16px',
                p: 2,
                mb: 2.5,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {visibleMonthRestDays.length > 0 ? (
                  <EventAvailableIcon sx={{ color: '#10B981', fontSize: 32 }} />
                ) : (
                  <EventBusyIcon sx={{ color: '#666', fontSize: 32 }} />
                )}
                <Box>
                  <Typography fontSize={14} color="#888">
                    設定済み休日
                  </Typography>
                  <Typography
                    fontSize={24}
                    fontWeight="bold"
                    color={visibleMonthRestDays.length > 0 ? '#10B981' : '#666'}
                  >
                    {visibleMonthRestDays.length > 0 ? `${visibleMonthRestDays.length}日` : '未設定'}
                  </Typography>
                </Box>
              </Box>
            </Card>

            <Button
              fullWidth
              variant="contained"
              startIcon={<CalendarIcon />}
              onClick={openRestCalendar}
              sx={{
                height: 56,
                bgcolor: '#10B981',
                color: '#000',
                fontWeight: 'bold',
                borderRadius: '12px',
                '&:hover': { bgcolor: '#0d9668' },
              }}
            >
              カレンダーで設定
            </Button>

            {visibleMonthRestDays.length > 0 && (
              <Box sx={{ mt: 2.5 }}>
                <Typography fontSize={16} color="#ccc" mb={1}>
                  設定済みの休日 (直近5件)
                </Typography>
                {visibleMonthRestDays
                  .slice()
                  .sort((a, b) => +b - +a)
                  .slice(0, 5)
                  .map((d) => (
                    <Card key={ymd(d)} sx={{ bgcolor: '#2a2a2a', p: 1, mb: 1, borderRadius: '10px' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarIcon sx={{ color: '#10B981', fontSize: 16 }} />
                        <Typography fontSize={15} color="white">
                          {ymd(d)}
                        </Typography>
                      </Box>
                    </Card>
                  ))}
              </Box>
            )}
          </Box>
        )}

        {tab === 'platform' && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h6" fontWeight="bold" color="white">
                  🏪 サービス管理
                </Typography>
                <Typography fontSize={15} color="#888">
                  配達記録で使用するサービス
                </Typography>
              </Box>
              <IconButton onClick={() => setShowAddPlatform(true)} sx={{ color: '#10B981' }}>
                <AddCircleIcon />
              </IconButton>
            </Box>

            {platforms.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8, color: '#888' }}>
                <StoreIcon sx={{ fontSize: 48, mb: 2 }} />
                <Typography fontSize={16}>サービスが登録されていません</Typography>
              </Box>
            ) : (
              platforms.map((p) => (
                <Card key={p.id ?? p.name} sx={{ bgcolor: '#2a2a2a', p: 2, mb: 1, borderRadius: '12px' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <StoreIcon sx={{ color: '#10B981' }} />
                      <Typography fontWeight={500} color="white">
                        {p.name}
                      </Typography>
                    </Box>
                    <IconButton
                      onClick={async () => {
                        if (p.id) {
                          try {
                            await deletePlatform(p.id);
                            await loadData();
                            onShowSnackbar('サービスを削除しました', 'success');
                          } catch {
                            onShowSnackbar('削除に失敗しました', 'error');
                          }
                        }
                      }}
                      sx={{ color: '#EF4444' }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Card>
              ))
            )}
          </Box>
        )}

        {tab === 'expense' && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h6" fontWeight="bold" color="white">
                  📁 支出カテゴリ管理
                </Typography>
                <Typography fontSize={15} color="#888">
                  支出記録で使用するカテゴリ
                </Typography>
              </Box>
              <IconButton onClick={() => setShowAddCategory(true)} sx={{ color: '#EF4444' }}>
                <AddCircleIcon />
              </IconButton>
            </Box>

            {expenseCategories.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8, color: '#888' }}>
                <CategoryIcon sx={{ fontSize: 48, mb: 2 }} />
                <Typography fontSize={16}>カテゴリが登録されていません</Typography>
              </Box>
            ) : (
              expenseCategories.map((c) => (
                <Card key={c.id ?? c.name} sx={{ bgcolor: '#2a2a2a', p: 2, mb: 1, borderRadius: '12px' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Typography fontSize={24}>{c.icon}</Typography>
                      <Typography fontWeight={500} color="white">
                        {c.name}
                      </Typography>
                    </Box>
                    <IconButton
                      onClick={async () => {
                        if (c.id) {
                          try {
                            await deleteExpenseCategory(c.id);
                            await loadData();
                            onShowSnackbar('カテゴリを削除しました', 'success');
                          } catch {
                            onShowSnackbar('削除に失敗しました', 'error');
                          }
                        }
                      }}
                      sx={{ color: '#EF4444' }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Card>
              ))
            )}
          </Box>
        )}

        {tab === 'data' && (
          <Box>
            <Typography variant="h6" fontWeight="bold" mb={1} color="white">
              💾 データ管理
            </Typography>
            <Typography fontSize={15} color="#888" mb={2.5}>
              データのバックアップと復元
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<UploadIcon />}
                onClick={handleExportData}
                sx={{
                  height: 56,
                  bgcolor: '#10B981',
                  color: '#000',
                  fontWeight: 'bold',
                  borderRadius: '12px',
                  '&:hover': { bgcolor: '#0d9668' },
                }}
              >
                データをエクスポート
              </Button>

              <input
                type="file"
                accept="application/json"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) {
                    setPendingImportFile(f);
                    setShowImportChoice(true);
                    e.currentTarget.value = '';
                  }
                }}
              />
              <Button
                fullWidth
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  height: 56,
                  bgcolor: '#3B82F6',
                  color: '#fff',
                  fontWeight: 'bold',
                  borderRadius: '12px',
                  '&:hover': { bgcolor: '#2563EB' },
                }}
              >
                データをインポート
              </Button>
            </Box>

            <Card sx={{ bgcolor: '#2a2a2a', p: 2, mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <InfoIcon sx={{ fontSize: 18, color: '#888' }} />
                <Typography fontSize={16} fontWeight="bold" color="#ccc">
                  データのバックアップについて
                </Typography>
              </Box>
              <Typography fontSize={14} color="#888" sx={{ lineHeight: 1.8 }}>
                📤 エクスポート: すべての配達記録、目標、休日、プラットフォーム、支出データをJSONファイルとして保存
                <br />
                📥 インポート: エクスポートしたJSONファイルからデータを復元（追加/置換を選択）
              </Typography>
            </Card>

            <Typography variant="h6" fontWeight="bold" mb={0.5} color="white">
              📊 確定申告用エクスポート　現在開発中
            </Typography>
            <Typography fontSize={15} color="#888" mb={2.5}>
              収支データをCSV形式で出力　現在開発中
            </Typography>

            <Button
              fullWidth
              variant="contained"
              startIcon={<ReceiptIcon />}
              onClick={handleExportTaxCSV}
              sx={{
                height: 56,
                bgcolor: '#8B5CF6',
                color: '#fff',
                fontWeight: 'bold',
                borderRadius: '12px',
                '&:hover': { bgcolor: '#7C3AED' },
              }}
            >
              確定申告用CSV出力　現在開発中
            </Button>

            <Card sx={{ bgcolor: '#2a0a0a', p: 2, mt: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'start', gap: 1.5 }}>
                <WarningIcon sx={{ fontSize: 20, color: '#EF4444', mt: 0.2 }} />
                <Typography fontSize={14} color="#EF4444" sx={{ lineHeight: 1.6 }}>
                  定期的なバックアップを推奨します。データの損失に備えて、重要なデータは複数の場所に保存してください。
                </Typography>
              </Box>
            </Card>
          </Box>
        )}
      </Box>

      {/* カレンダーダイアログ */}
      <Dialog
        open={openCal}
        onClose={() => setOpenCal(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { bgcolor: '#1a1a1a', color: 'white', borderRadius: '20px' } }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <IconButton
              onClick={() =>
                setCalYM(({ y, m }) => (m === 0 ? { y: y - 1, m: 11 } : { y, m: m - 1 }))
              }
              sx={{ color: 'white' }}
            >
              <ChevronLeftIcon />
            </IconButton>
            <Typography variant="h6" fontWeight="bold" color="#10B981">
              {calYM.y}年{calYM.m + 1}月
            </Typography>
            <IconButton
              onClick={() =>
                setCalYM(({ y, m }) => (m === 11 ? { y: y + 1, m: 0 } : { y, m: m + 1 }))
              }
              sx={{ color: 'white' }}
            >
              <ChevronRightIcon />
            </IconButton>
          </Box>

          <Divider sx={{ borderColor: '#333', mb: 1 }} />

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 1 }}>
            {['日', '月', '火', '水', '木', '金', '土'].map((d) => (
              <Typography key={d} align="center" fontSize={14} color="#ccc">
                {d}
              </Typography>
            ))}
          </Box>

          {(() => {
            const totalCells = firstDow + daysInMonth;
            const weeks = Math.ceil(totalCells / 7) * 7;

            return (
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.75 }}>
                {Array.from({ length: weeks }).map((_, i) => {
                  const day = i - firstDow + 1;
                  const isDay = day > 0 && day <= daysInMonth;
                  if (!isDay) return <Box key={`pad-${i}`} />;
                  const key = ymd(new Date(calYM.y, calYM.m, day));
                  const selected = calendarSelectedSet.has(key);
                  return (
                    <Card
                      key={day}
                      onClick={() => toggleDate(day)}
                      sx={{
                        aspectRatio: '1 / 1',
                        bgcolor: selected ? '#10B981' : '#2a2a2a',
                        color: selected ? '#000' : '#fff',
                        borderRadius: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                      }}
                      elevation={selected ? 2 : 0}
                    >
                      <Typography fontWeight={selected ? 'bold' : 'normal'}>{day}</Typography>
                    </Card>
                  );
                })}
              </Box>
            );
          })()}

          <Card sx={{ bgcolor: '#0f3d2c', mt: 2 }}>
            <CardContent sx={{ py: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
                <EventAvailableIcon sx={{ color: '#10B981', fontSize: 20 }} />
                <Typography fontWeight="bold" color="#10B981">
                  {calendarSelectedSet.size}日選択中
                </Typography>
              </Box>
            </CardContent>
          </Card>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mt: 2 }}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<CheckIcon />}
              onClick={handleSaveRestDays}
              sx={{
                height: 48,
                bgcolor: '#10B981',
                color: '#000',
                fontWeight: 'bold',
                borderRadius: '12px',
              }}
            >
              決定
            </Button>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleCancelRestDays}
              sx={{ height: 44, borderColor: '#888', color: '#888', borderRadius: '12px' }}
            >
              キャンセル
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* サービス追加ダイアログ */}
      <Dialog
        open={showAddPlatform}
        onClose={() => setShowAddPlatform(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { bgcolor: '#1a1a1a', color: 'white', borderRadius: '20px' } }}
      >
        <Box sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <AddCircleIcon sx={{ color: '#10B981' }} />
            <Typography fontWeight="bold">サービスを追加</Typography>
          </Box>
          <TextField
            fullWidth
            label="サービス名"
            value={platformName}
            onChange={(e) => setPlatformName(e.target.value)}
            InputProps={{ startAdornment: <StoreIcon sx={{ mr: 1, color: '#10B981' }} /> }}
            sx={{
              '& .MuiInputBase-root': { color: 'white', bgcolor: '#2a2a2a' },
              '& .MuiInputLabel-root': { color: '#ccc' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#444' },
              '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#10B981',
              },
            }}
          />
          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Button
              fullWidth
              variant="contained"
              disabled={!platformName.trim()}
              onClick={async () => {
                try {
                  await addPlatform({
                    name: platformName.trim(),
                    color: '#10B981',
                    isActive: true,
                    createdAt: Date.now(),
                  });
                  await loadData();
                  setPlatformName('');
                  setShowAddPlatform(false);
                  onShowSnackbar('サービスを追加しました', 'success');
                } catch {
                  onShowSnackbar('追加に失敗しました', 'error');
                }
              }}
              sx={{ bgcolor: '#10B981', color: '#000', fontWeight: 'bold' }}
            >
              追加
            </Button>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => setShowAddPlatform(false)}
              sx={{ color: '#888' }}
            >
              キャンセル
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* カテゴリ追加ダイアログ */}
      <Dialog
        open={showAddCategory}
        onClose={() => setShowAddCategory(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { bgcolor: '#1a1a1a', color: 'white', borderRadius: '20px' } }}
      >
        <Box sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <AddCircleIcon sx={{ color: '#EF4444' }} />
            <Typography fontWeight="bold">支出カテゴリを追加</Typography>
          </Box>

          <Typography fontSize={15} color="#ccc" sx={{ mb: 1 }}>
            アイコン
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 1, mb: 2 }}>
            {categoryIcons.map((ic) => (
              <Card
                key={ic}
                onClick={() => setCategoryIcon(ic)}
                sx={{
                  p: 1,
                  borderRadius: '8px',
                  textAlign: 'center',
                  bgcolor: categoryIcon === ic ? '#EF4444' : '#333',
                  cursor: 'pointer',
                }}
              >
                <Typography fontSize={22}>{ic}</Typography>
              </Card>
            ))}
          </Box>

          <TextField
            fullWidth
            label="カテゴリ名"
            value={categoryName}
            onChange={(e) => setCategoryName(e.target.value)}
            InputProps={{ startAdornment: <CategoryIcon sx={{ mr: 1, color: '#EF4444' }} /> }}
            sx={{
              '& .MuiInputBase-root': { color: 'white', bgcolor: '#2a2a2a' },
              '& .MuiInputLabel-root': { color: '#ccc' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#444' },
              '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#EF4444',
              },
            }}
          />

          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Button
              fullWidth
              variant="contained"
              disabled={!categoryName.trim()}
              onClick={async () => {
                try {
                  await addExpenseCategory({
                    name: categoryName.trim(),
                    icon: categoryIcon,
                    color: '#EF4444',
                    isActive: true,
                    createdAt: Date.now(),
                  });
                  await loadData();
                  setCategoryName('');
                  setCategoryIcon('💰');
                  setShowAddCategory(false);
                  onShowSnackbar('カテゴリを追加しました', 'success');
                } catch {
                  onShowSnackbar('追加に失敗しました', 'error');
                }
              }}
              sx={{ bgcolor: '#EF4444', color: '#fff', fontWeight: 'bold' }}
            >
              追加
            </Button>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => setShowAddCategory(false)}
              sx={{ color: '#888' }}
            >
              キャンセル
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* インポート方式選択ダイアログ */}
      <Dialog
        open={showImportChoice}
        onClose={() => {
          setShowImportChoice(false);
          setPendingImportFile(null);
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { bgcolor: '#1a1a1a', color: 'white', borderRadius: '20px' } }}
      >
        <Box sx={{ p: 2.5 }}>
          <Typography fontWeight="bold" mb={1}>
            インポート方法を選択
          </Typography>
          <Typography fontSize={15} color="#ccc" mb={2}>
            既存データをどうしますか？
          </Typography>

          <RadioGroup
            value={replaceExisting ? 'replace' : 'append'}
            onChange={(e) => setReplaceExisting(e.target.value === 'replace')}
          >
            <FormControlLabel
              value="append"
              control={<Radio sx={{ color: '#10B981', '&.Mui-checked': { color: '#10B981' } }} />}
              label={<Typography>既存データに追加</Typography>}
            />
            <FormControlLabel
              value="replace"
              control={<Radio sx={{ color: '#EF4444', '&.Mui-checked': { color: '#EF4444' } }} />}
              label={
                <Box>
                  <Typography>既存データを置き換え</Typography>
                  <Typography fontSize={13} color="#EF4444">
                    ※既存のデータは削除されます
                  </Typography>
                </Box>
              }
            />
          </RadioGroup>

          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleImportData}
              sx={{
                bgcolor: replaceExisting ? '#EF4444' : '#10B981',
                color: '#fff',
                fontWeight: 'bold',
              }}
            >
              インポート開始
            </Button>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                setShowImportChoice(false);
                setPendingImportFile(null);
              }}
              sx={{ color: '#888' }}
            >
              キャンセル
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
}

export default SettingsScreen;

